/**
 * Location Entity Type Definitions
 * 
 * Type definitions for the Location business entity.
 * Includes geographic data, seasonality, monetary config, and billing profiles.
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export type LocationType = 'campsite' | 'surfcamp' | 'surfhouse' | 'hostel' | 'resort';

export type LocationStatus = 'active' | 'inactive' | 'seasonal_closed' | 'pending_setup';

/**
 * Operational status independent of soft-delete (Location.is_active).
 *
 *  - 'active'     — open to guests and accepting bookings (default)
 *  - 'off_season' — operations limited; bookings allowed but reduced services
 *  - 'closed'     — fully closed window; new bookings blocked
 *
 * This is the canonical user-facing status used by the General settings
 * page. It is NOT the same as `LocationStatus` (which is a derived view
 * that also encodes pending_setup / soft-deleted states for list filtering).
 */
export type OperationalStatus = 'active' | 'off_season' | 'closed';

// ── Guest payment defaults (Settings · Guest payment) ──────────────────────

export type BalanceDueWindow = '14_days_before' | '7_days_before' | 'on_checkin';
export type PrepaidRefundPolicy = 'auto_on_checkout' | 'on_request' | 'forfeit';
export type AllowedPaymentMethod = 'cash' | 'card' | 'bank_transfer' | 'guest_prepaid' | 'room_charge';
export type PrepaidRedemptionSurface = 'restaurant_bar' | 'shop' | 'activities' | 'spa';

export interface PrepaidBonusTier {
  threshold: number;
  bonus: number;
}

// ── Crew app feature flags (Settings · Staff → Crew app block) ────────────

/** Scope of activities a crew staff member sees on their schedule. */
export type CrewActivityVisibility = 'own' | 'location' | 'role';

/** Per-location feature toggles consumed by the shaka-crew mobile app.
 * Persisted as four columns on ``Location``; deck reads/writes them via
 * the standard ``locations/{id}/`` PATCH surface. The crew device pulls
 * the derived ``staffAppConfig`` bundle from
 * ``GET /api/v1/staff/app-config/?location_id=…`` which combines these
 * with token-related flags from ``LocationStaffTokenConfig``.
 *
 * Naming uses *attendance* rather than *check-in* on purpose —
 * "check-in" is the booking front-desk operation; here the flag gates
 * **activity attendance** (cf. ``ActivityEnrollment.attended_via``).
 */
export interface CrewAppConfig {
  nfcAttendanceEnabled: boolean;
  manualAttendanceEnabled: boolean;
  activityVisibility: CrewActivityVisibility;
  shiftSwapEnabled: boolean;
}

// ── Staff defaults (Settings · Staff, non-token) ───────────────────────────

export interface StaffDefaultsConfig {
  // Shift defaults
  minRestBetweenShiftsHours: number;
  autoApproveShiftCoverage: boolean;

  // Approval thresholds
  activityCancelAfterStartRequiresManager: boolean;
  folioCapOverrideRequiresManager: boolean;
}

export interface GuestPaymentConfig {
  // Master toggles (already on Location root, mirrored here for the form)
  roomChargeEnabled: boolean;
  prepaidEnabled: boolean;
  paymentModeDefault: 'room_charge' | 'prepaid';

  // Deposit policy
  defaultDepositPct: number;
  balanceDueWindow: BalanceDueWindow;
  cancellationPolicyText: string;

  // Folio sub-toggles + cap
  folioChargeFnb: boolean;
  folioChargeActivities: boolean;
  folioChargeShop: boolean;
  folioCapPerStay: number;

  // Allowed methods
  allowedPaymentMethods: AllowedPaymentMethod[];

  // Prepaid wallet rules (only meaningful when prepaidEnabled === true)
  prepaidMinRecharge: number;
  prepaidMaxBalance: number;
  prepaidDefaultTopups: number[];
  prepaidRefundPolicy: PrepaidRefundPolicy;
  prepaidBonusTiers: PrepaidBonusTier[];
  prepaidRedemptionSurfaces: PrepaidRedemptionSurface[];
}

/**
 * Settings · Operating hours week orientation.
 *
 *   'monday'    — ISO / Europe default (default value)
 *   'sunday'    — US / Latin America
 *   'saturday'  — Middle East
 */
export type WeekStartDay = 'monday' | 'sunday' | 'saturday';

/** Three-letter day code used by `Location.weekendDays`. */
export type WeekendDayCode = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

// ── Display & formatting preferences (Settings · Formats) ────────────────────
/** Short numeric date layout. */
export type DateFormatPref = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
/** Clock format. */
export type TimeFormatPref = '24h' | '12h';
/**
 * Decimal/thousands separator style:
 *   'point' → 1,234.56   'comma' → 1.234,56   'space' → 1 234,56
 */
export type NumberFormatPref = 'point' | 'comma' | 'space';
/** Default unit suggestions in product/measurement forms. */
export type UnitSystemPref = 'metric' | 'imperial';

/**
 * Open-ended service identifier for operating hours rows. The backend
 * uses CharField (no enum) so adding a new key here is safe — the
 * shipped UI only renders the four below.
 */
export type OperatingHoursServiceKey =
  | 'reception'
  | 'restaurant'
  | 'bar'
  | 'kitchen';

/** ISO weekday: 0 = Monday … 6 = Sunday. */
export type DayOfWeekIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface OperatingHoursRow {
  id?: string;
  locationId: string;
  service: OperatingHoursServiceKey | string;
  dayOfWeek: DayOfWeekIndex;
  isClosed: boolean;
  /** "HH:MM" 24-hour. Null when isClosed=true. */
  openTime: string | null;
  /** "HH:MM" 24-hour. Null when isClosed=true. May be < openTime to denote
   *  a window crossing midnight (caller decides). */
  closeTime: string | null;
}

export interface HolidayOverrideRow {
  id?: string;
  locationId: string;
  /** "YYYY-MM-DD". */
  date: string;
  reason: string;
  isClosed: boolean;
  openTime: string | null;
  closeTime: string | null;
}

export type CommercialModule =
  | 'core_stay'
  | 'activities'
  | 'pos_commerce'
  | 'inventory'
  | 'treasury'
  | 'hr';

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday

// ============================================================================
// GEOGRAPHIC DATA
// ============================================================================

export interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

export interface LocationAddress {
  street: string;
  number?: string;
  floor?: string;
  apartment?: string;
  city: string;
  state?: string;
  country: string;
  postalCode?: string;
  coordinates?: GeoCoordinates;
  formattedAddress?: string; // Google Maps formatted address
  placeId?: string; // Google Maps place ID for reference
}

// ============================================================================
// OPERATING HOURS
// ============================================================================

export interface DailySchedule {
  dayOfWeek: DayOfWeek;
  isOpen: boolean;
  openTime?: string; // HH:mm format
  closeTime?: string; // HH:mm format
  breakStart?: string; // Optional break period
  breakEnd?: string;
}

export interface LocationSchedule {
  weeklySchedule: DailySchedule[];
  holidaysClosed: boolean;
  customClosedDates?: string[]; // ISO date strings
  notes?: string;
}

// ============================================================================
// SEASONALITY
// ============================================================================

export type SeasonMode = 'annual' | 'seasonal';

export type OffSeasonBehavior = 'closed' | 'staff_only' | 'limited';

export type ClosureType = 
  | 'maintenance' 
  | 'renovation' 
  | 'private_event' 
  | 'emergency' 
  | 'holiday' 
  | 'weather' 
  | 'other';

/**
 * Temporary closure during an active season
 */
export interface TemporaryClosure {
  id: string;
  startDate: string;           // YYYY-MM-DD
  endDate: string;             // YYYY-MM-DD
  type: ClosureType;
  reason: string;              // Brief description
  description?: string;        // Detailed explanation
  affectsBookings: boolean;    // Should existing bookings be notified/cancelled
  affectsPublicAccess: boolean; // Is the location closed to public
  staffRequired: boolean;      // Does staff still need to work
  notifyGuests: boolean;       // Send notifications to affected guests
  internalNotes?: string;      // Notes for staff only
  createdAt: Date;
  createdBy?: string;
  updatedAt?: Date;
}

export interface TemporaryClosureInput {
  startDate: string;
  endDate: string;
  type: ClosureType;
  reason: string;
  description?: string;
  affectsBookings?: boolean;
  affectsPublicAccess?: boolean;
  staffRequired?: boolean;
  notifyGuests?: boolean;
  internalNotes?: string;
}

/**
 * Pre-season configuration
 * Period where location is operational for staff but not accepting bookings
 */
export interface PreSeasonConfig {
  enabled: boolean;
  startDate?: string;          // YYYY-MM-DD or MM-DD depending on mode
  endDate?: string;            // Typically = seasonStart - 1 day
  activeModules?: string[];    // Which modules are active (default: all except bookings/guests)
  notes?: string;
}

export interface SeasonConfig {
  mode: SeasonMode;            // 'annual' or 'seasonal'
  
  // Season dates (MM-DD format for recurring dates)
  seasonStart: string;         // MM-DD - When season begins
  seasonEnd?: string;          // MM-DD - Only required for 'seasonal' mode
                               // For 'annual' mode, end = start (full year cycle)
  
  // Pre-season configuration
  preSeason?: PreSeasonConfig;
  
  // Off-season behavior (what happens outside the season)
  offSeasonBehavior: OffSeasonBehavior;
  offSeasonActiveModules?: string[]; // If behavior is 'limited', which modules stay active
  
  // Temporary closures during active season
  temporaryClosures?: TemporaryClosure[];
  
  // Legacy compatibility
  isYearRound?: boolean;       // Deprecated: use mode === 'annual'
  autoCloseOutOfSeason?: boolean; // Deprecated: use offSeasonBehavior
  preSeasonOpenDays?: number;  // Deprecated: use preSeason
}

/** Helper to check if currently in season */
export function isInSeason(config: SeasonConfig, date: Date = new Date()): boolean {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const currentMMDD = `${month}-${day}`;
  
  if (config.mode === 'annual') {
    return true; // Annual mode is always "in season"
  }
  
  const start = config.seasonStart;
  const end = config.seasonEnd;
  
  if (!start || !end) return true;
  
  // Handle season that crosses year boundary (e.g., Nov - Mar)
  if (start > end) {
    return currentMMDD >= start || currentMMDD <= end;
  }
  
  return currentMMDD >= start && currentMMDD <= end;
}

/** Format a Date to YYYY-MM-DD using local timezone (avoids UTC shift). */
function toLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Helper to check if currently in pre-season */
export function isInPreSeason(config: SeasonConfig, date: Date = new Date()): boolean {
  if (!config.preSeason?.enabled || !config.preSeason.startDate || !config.preSeason.endDate) {
    return false;
  }

  const dateStr = toLocalYmd(date);
  return dateStr >= config.preSeason.startDate && dateStr <= config.preSeason.endDate;
}

/** Helper to check if there's an active temporary closure */
export function getActiveClosure(config: SeasonConfig, date: Date = new Date()): TemporaryClosure | null {
  if (!config.temporaryClosures?.length) return null;

  const dateStr = toLocalYmd(date);

  return config.temporaryClosures.find(
    closure => dateStr >= closure.startDate && dateStr <= closure.endDate
  ) || null;
}

// ============================================================================
// MONETARY CONFIGURATION
// ============================================================================

export interface CurrencyConfig {
  code: string; // ISO 4217 code (e.g., "USD", "EUR", "ARS")
  symbol?: string; // Optional custom symbol (e.g., "$", "€")
  locale?: string; // Locale for formatting (e.g., "en-US", "es-MX")
  exchangeRate?: number; // Exchange rate relative to default currency
  isDefault: boolean; // Whether this is the default currency
}

export type TaxDisplayMode = 'inclusive' | 'exclusive';
export type CashRoundingMode = 'none' | 'nearest_05' | 'nearest_10';

export interface MonetaryConfig {
  currencies: CurrencyConfig[]; // At least one currency required, one must be default
  timezone: string; // IANA timezone (e.g., "America/Argentina/Buenos_Aires")
  taxRate?: number; // Default tax rate percentage (legacy)
  acceptedPaymentMethods?: string[]; // cash, card, transfer, etc.

  // Settings · Monetary
  showPricesInGuestCurrency?: boolean;
  taxLabel?: string;
  taxDisplayMode?: TaxDisplayMode;
  cashRounding?: CashRoundingMode;
  displayPrecision?: number;
  roundLineItemsIndividually?: boolean;
  /**
   * Propinas. El cargo por servicio automático se borró el 2026-11-03: es
   * ilegal en España, en Francia cuenta como salario y entra en la base del
   * IVA, al revés que la propina voluntaria. Estos dos se quedan para la
   * fase 1 de propinas, que trae el override por terminal.
   */
  tippingEnabled?: boolean;
  suggestedTips?: number[];
}

/** Helper to get default currency from monetary config */
export function getDefaultCurrency(monetary: MonetaryConfig): CurrencyConfig {
  const defaultCurrency = monetary.currencies.find(c => c.isDefault);
  return defaultCurrency ?? monetary.currencies[0];
}

// ============================================================================
// CONTACT & CAPACITY
// ============================================================================

export interface LocationContact {
  phone?: string;
  phoneSecondary?: string;
  email?: string;
  website?: string;
  socialMedia?: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
    whatsapp?: string;
    tiktok?: string;
    youtube?: string;
  };
  departmentContacts?: Partial<Record<
    DepartmentKey,
    DepartmentContact
  >>;
}

/**
 * Department keys surfaced in the Contact settings page. Stored in the
 * `contact_config.department_contacts` JSON blob on the backend (no enum
 * enforcement at the model level), so adding a new key here is safe.
 */
export type DepartmentKey =
  | 'frontdesk'
  | 'restaurant'
  | 'bar'
  | 'manager'
  | 'activities'
  | 'housekeeping'
  | 'maintenance'
  | 'finance'
  | 'hr'
  | 'operations'
  | 'sales';

export interface DepartmentContact {
  name?: string;
  email?: string;
  phone?: string;
}

/**
 * Capacity & limits — Settings · Capacity. All optional in the type so legacy
 * Location records (pre-migration 0017) keep working; defaults are applied
 * client-side when the section mounts.
 */
/**
 * The closed catalogue of guest fields a location can mark as required.
 * Mirrors `Location.GUEST_FORM_FIELD_CHOICES` on the backend, which refuses
 * any key outside it.
 */
export type GuestFormField =
  | 'document'
  | 'document_expiration'
  | 'birth_date'
  | 'nationality'
  | 'email'
  | 'phone';

export const GUEST_FORM_FIELDS: GuestFormField[] = [
  'document',
  'document_expiration',
  'birth_date',
  'nationality',
  'email',
  'phone',
];

export interface LocationCapacity {
  // Physical capacity
  totalRooms?: number;
  totalBeds?: number;
  maxGuests?: number;
  maxPerRoom?: number;

  // Occupancy alerts (% of maxGuests)
  occupancyWarningPct?: number;
  occupancyCriticalPct?: number;
  blockOvercapacityBookings?: boolean;
  allowExtraGuestBeds?: boolean;

  // Booking-length limits
  minStayNights?: number;
  maxStayNights?: number;
  defaultStayNights?: number;
  bookingWindowDays?: number;

  // Group bookings
  maxPerBooking?: number;
  groupThreshold?: number;
  groupRequiresApproval?: boolean;

  // (Removed 2026-10-29: `maxTables`, `maxRooms`, `maxStaff` and
  // `parkingSpaces` were deck-only slots the API has never sent. Their three
  // readers rendered a `&&` guard that could not be true, and one fallback
  // that was always 0. The real fields are `totalRooms` / `totalBeds` /
  // `maxGuests` above.)
}

// ============================================================================
// BILLING PROFILE
// ============================================================================

export interface BillingAddress {
  street: string;
  number?: string;
  city: string;
  state?: string;
  country: string;
  postalCode?: string;
}

export interface BillingProfile {
  id: string;
  locationId?: string;
  name: string; // Internal name for identification (e.g., "Factura A", "Consumidor Final")
  legalName: string; // Legal business name
  taxId: string; // CUIT, RFC, NIF, EIN, etc.
  taxIdType: string; // Type of tax ID (e.g., "CUIT", "RFC", "VAT")
  address: BillingAddress;
  email?: string; // Email for billing notifications
  phone?: string;
  isDefault?: boolean;
  isShakaBilling?: boolean;
  isActive: boolean;
  isCompanyDefault?: boolean; // The tenant's company-level fiscal identity (seeded at onboarding)
  locationAssignments?: BillingProfileLocationAssignment[];
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface BillingProfileInput {
  name: string;
  legalName: string;
  taxId: string;
  taxIdType: string;
  address: BillingAddress;
  email?: string;
  phone?: string;
  isDefault?: boolean;
  isActive?: boolean;
  notes?: string;
}

export interface BillingProfileFilters {
  locationId?: string;
  isActive?: boolean;
  search?: string;
}

export interface BillingProfileLocationAssignment {
  id: string;
  locationId: string;
  billingProfileId: string;
  isDefault: boolean;
  isShakaBilling: boolean;
  billingProfile?: BillingProfile;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// MAIN LOCATION ENTITY
// ============================================================================

export interface Location {
  id: string;

  // Basic Info
  name: string;
  slug: string; // URL-friendly unique identifier
  description?: string;
  type: LocationType;
  status: LocationStatus;
  operationalStatus: OperationalStatus;
  brandColor?: string; // Hex color (e.g. "#5A8A8C") used by transactional emails + brand surfaces

  // Week orientation — drives shifts/payroll grids and "this week" math
  weekStartDay: WeekStartDay;
  /**
   * Standard arrival/departure times ("HH:MM", PENDINGS #7) — the bookings
   * calendar places timelines with these; editable in Settings → Operating
   * hours.
   */
  defaultCheckInTime: string;
  defaultCheckOutTime: string;
  weekendDays: WeekendDayCode[];

  // Display & formatting preferences (Settings · Formats)
  dateFormat: DateFormatPref;
  timeFormat: TimeFormatPref;
  numberFormat: NumberFormatPref;
  unitSystem: UnitSystemPref;

  // Relations
  activeModules: CommercialModule[];
  locale?: string;
  
  // Geographic
  address: LocationAddress;
  
  // Operations
  schedule?: LocationSchedule;
  season?: SeasonConfig;
  monetary: MonetaryConfig;
  
  // Contact & Capacity
  contact?: LocationContact;
  capacity?: LocationCapacity;
  
  // Media
  logoUrl?: string;
  
  // Metadata
  tags?: string[];

  /**
   * Guest fields this site requires, beyond the always-required name (#35).
   *
   * Which fields are mandatory used to be a constant written separately into
   * each form, and the forms disagreed — the batch check-in blocked on a
   * missing ID document after the individual form had stopped doing so.
   *
   * GDPR (Art. 5.1.c): every entry is a data-MINIMISATION decision, not a UX
   * one — requiring a field obliges the site to collect it from every guest.
   * Absent or empty means name only, which is the behaviour that existed
   * before this setting.
   */
  guestFormRequiredFields?: GuestFormField[];

  // Guest payment configuration
  guestRoomChargeEnabled: boolean;
  guestPrepaidEnabled: boolean;
  guestPaymentModeDefault: 'room_charge' | 'prepaid';
  guestPayment: GuestPaymentConfig;

  // Staff defaults (non-token)
  staffDefaults: StaffDefaultsConfig;

  // Crew app feature flags (Settings · Staff → Crew app block)
  crewApp: CrewAppConfig;

  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// INPUT & FILTER TYPES
// ============================================================================

/** Blocks the CreateLocationWizard can ask the backend to copy from a
 *  source location at creation time. Each entry is a discrete service
 *  on the backend (`apps/locations/services/setup_copy.py`). */
export type LocationSetupCopyBlock = 'categories' | 'staff_positions';

export interface LocationSetupInitialAccess {
  mode: 'existing' | 'new';
  /** Required when mode='existing'. */
  userId?: string;
  /** Required when mode='new'. */
  name?: string;
  email?: string;
  /** AccessRole to assign at location scope. */
  roleId: string;
}

export interface LocationSetupBillingSelection {
  billingProfileId: string;
  isDefault: boolean;
  isShakaBilling: boolean;
}

export interface LocationSetupInput {
  sourceLocationId?: string;
  copy?: LocationSetupCopyBlock[];
  initialAccess?: LocationSetupInitialAccess;
  /** Billing profile links created atomically with the location. Exactly one
   *  must be flagged isShakaBilling. Omit to auto-link the company default. */
  billing?: LocationSetupBillingSelection[];
}

export interface LocationSetupReportBlock {
  copied: number;
  skipped: number;
  errors: string[];
}

export interface LocationSetupReport {
  categories?: LocationSetupReportBlock;
  staffPositions?: LocationSetupReportBlock;
  initialAccess?: {
    userId: string | null;
    accessId: string | null;
    createdUser: boolean;
    hasPasswordSetupToken: boolean;
    /** Only present when a new user was created — the wizard can copy
     *  it into a "send invite link" modal. */
    passwordSetupToken?: string;
  };
}

export interface LocationInput {
  name: string;
  slug?: string; // Auto-generated from name if not provided
  description?: string;
  type: LocationType;
  status?: LocationStatus;
  operationalStatus?: OperationalStatus;
  brandColor?: string;
  weekStartDay?: WeekStartDay;
  weekendDays?: WeekendDayCode[];
  defaultCheckInTime?: string;
  defaultCheckOutTime?: string;
  dateFormat?: DateFormatPref;
  timeFormat?: TimeFormatPref;
  numberFormat?: NumberFormatPref;
  unitSystem?: UnitSystemPref;
  guestPayment?: Partial<GuestPaymentConfig>;
  staffDefaults?: Partial<StaffDefaultsConfig>;
  crewApp?: Partial<CrewAppConfig>;
  activeModules?: CommercialModule[];
  locale?: string;
  address: LocationAddress;
  schedule?: LocationSchedule;
  season?: SeasonConfig;
  monetary: MonetaryConfig;
  contact?: LocationContact;
  capacity?: LocationCapacity;
  /** See Location.guestFormRequiredFields — GDPR Art. 5.1.c. */
  guestFormRequiredFields?: GuestFormField[];
  logoUrl?: string;
  tags?: string[];
  /** Optional setup-copy + initial-access block. When present, the
   *  backend composes these inside a single atomic transaction with
   *  the location creation. Errors surface as 400 with a
   *  `setup.error` discriminator. */
  setup?: LocationSetupInput;
}

export interface LocationFilters {
  search?: string;
  type?: LocationType;
  status?: LocationStatus;
  city?: string;
  country?: string;
  isYearRound?: boolean;
  tags?: string[];
}

export interface LocationUpdateInput extends Partial<LocationInput> {
  id: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface LocationWithBillingProfiles extends Location {
  billingProfiles: BillingProfile[];
}

export interface LocationSummary {
  id: string;
  name: string;
  slug: string;
  type: LocationType;
  status: LocationStatus;
  city: string;
  country: string;
}
